/**
 * Vulcan's Contentful content-type → engine-kind map for
 * `@mobilelive-inc/telus-forms-core`'s `normalizeItems` (v2 tree normalizer).
 *
 * This is the single declaration of how Vulcan's content model maps onto the
 * engine's structure-preserving tree. It replaces the giant `if (id === …)`
 * cascade in `formStepVulcan.js`'s `renderFormFields` (≈ lines 1759–1915): the
 * engine now walks the tree and dispatches each item by `kind`, so the app no
 * longer hand-codes the structural branching.
 *
 * Kinds (see the engine's ItemDefinition):
 *  - `group`     → wrapped by the structural `Group` component; children under `fields`.
 *  - `section`   → the collapsible `ExpandCollapse.Panel`; children under `formFields`.
 *  - `sectionTitle` → a plain `<Typography h3>` heading (no children).
 *  - `note`      → the standalone TextNote content type.
 *  - `custom`    → a bespoke section rendered via the `sectionRegistry` (`form` prop).
 *
 * Content types NOT listed here fall through to the normalizer's default: an
 * entry with a `type` becomes a leaf `field`; otherwise a `custom` section keyed
 * by its content-type id. Vulcan's inline bespoke sections (CouponBasedDiscount,
 * OptikTvTable, AddOfficePhoneLine, the three Consolidation blocks) are leaf
 * fields with a `type` — they stay `field` here and are claimed by the
 * `sectionRegistry` at render time (engine alpha.2: a field `type` may be
 * registered as a section). So they are intentionally absent from this map.
 *
 * Child-field collection — the nested-state design is TWO MODES, chosen per how
 * the app actually stores each section's sub-fields (verified by reading the
 * components):
 *
 *  - **flat-join** (`collectChildFields: true`): the section's children are
 *    FLAT-NAMED fields in the global bag (userDetailsForm.js writes
 *    `formFields['${streetAddress}_${backendKey}']`, `formFields[fibreSpeed]`,
 *    … — plain top-level keys, and it even writes across sections). So its
 *    children opt into the engine's flat leaf state: they get initial values,
 *    validation, and pricing/payload visibility like any other field, and the
 *    section component reads/writes them via `form.fieldStates` / `form.setValue`.
 *    `userDetails` (children under `formStepFields`) and `repeatedForm` are this
 *    mode.
 *  - **self-contained sub-bag** (no `collectChildFields`): the section's children
 *    are GENUINELY NESTED under one key (equipmentSectionFormVulcan stores
 *    `values['${package}additionalEquipmentVulcan'][productKey]`). It owns that
 *    blob internally and exposes it as one ancillary value — exactly like the six
 *    field-type bespoke sections (`form.values[KEY]`). `equipmentGroupSection`
 *    stays this mode, so it is intentionally left without `collectChildFields`.
 *
 * (repeatedForm's `childrenKey` is confirmed when it is wired; only userDetails
 * declares one here for now.)
 *
 * @type {import('@mobilelive-inc/telus-forms-core').ContentTypeMap}
 */
export const vulcanContentTypeMap = {
  // Structural containers.
  formFieldsGroup: { kind: 'group', childrenKey: 'fields' },
  formSection: { kind: 'section', childrenKey: 'formFields' },

  // Structural leaves.
  formSectionTitle: { kind: 'sectionTitle' },
  note: { kind: 'note' },

  // Bespoke sections (content-type-based) — dispatched to the sectionRegistry.
  // flat-join: children join the engine's flat leaf state.
  userDetails: {
    kind: 'custom',
    sectionType: 'userDetails',
    childrenKey: 'formStepFields',
    collectChildFields: true
  },
  repeatedForm: { kind: 'custom', sectionType: 'repeatedForm' },
  // self-contained sub-bag: owns its nested state under one value key.
  equipmentGroupSection: {
    kind: 'custom',
    sectionType: 'equipmentGroupSection'
  }
};

export default vulcanContentTypeMap;
